import React, { useState, useCallback, memo, useEffect } from 'react';
import { Select, InputNumber, AutoComplete, Input, Button, Space, message, Divider, Popconfirm } from 'antd';
import { SaveOutlined, PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { getDataCommon } from '../../../redux/slices/dataCommonSlice';
import api from '../../../services/api';

const { Option } = Select;

// Enhanced Editable Cell Component with Note Template Support
const EditableCellWithNoteTemplates = memo(({ 
    value, 
    record, 
    column, 
    onSave, 
    options = [], 
    placeholder = "Select...",
    loading = false,
    inputType = "select", // "select", "number", "text", "autocomplete", "note-autocomplete"
    prefix = "", // New prop for prefix (like $)
    autoCompleteOptions = [], // For autocomplete functionality
    displayComponent = null // Custom display component function
}) => {
    const [editing, setEditing] = useState(false);
    const [currentValue, setCurrentValue] = useState(value);
    const [showSaveButton, setShowSaveButton] = useState(false);
    const [isSavingTemplate, setIsSavingTemplate] = useState(false);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [isDeletingTemplate, setIsDeletingTemplate] = useState(false);
    const [hoveredTemplateId, setHoveredTemplateId] = useState(null);
    
    // Get noteTemplates from Redux store
    const { noteTemplates } = useSelector(state => state.dataCommon);
    const dispatch = useDispatch();

    // Sync currentValue with value prop when it changes (e.g., after API reload)
    useEffect(() => {
        setCurrentValue(value);
    }, [value]);

    // Open dropdown when entering edit mode for note fields
    useEffect(() => {
        if (editing && inputType === "note-autocomplete") {
            setDropdownOpen(true);
        }
    }, [editing, inputType]);

    const fetchNoteTemplates = async () => {
        // This function is no longer needed since we use Redux store
        // But keeping it for potential refresh functionality
        try {
            const response = await api.get('/note-templates?limit=50&orderBy=usageCount&orderDirection=DESC');
            if (response.data.success) {
                // Could dispatch action to update Redux store if needed
                console.log('Note templates refreshed from API');
            }
        } catch (error) {
            console.error('Error fetching note templates:', error);
        }
    };

    const handleSave = useCallback(async (newValue) => {
        if (newValue !== value) {
            const success = await onSave(record, column, newValue);
            if (success) {
                setCurrentValue(newValue);
                
                // If using a note template, increment its usage count
                if (inputType === "note-autocomplete" && newValue) {
                    const selectedTemplate = noteTemplates.find(t => t.content === newValue);
                    if (selectedTemplate) {
                        try {
                            await api.patch(`/note-templates/${selectedTemplate.id}/usage`);
                        } catch (error) {
                            console.error('Error incrementing template usage:', error);
                        }
                    }
                }
            }
        }
        setEditing(false);
        setShowSaveButton(false);
        setHoveredTemplateId(null);
    }, [value, record, column, onSave, inputType, noteTemplates]);

    const handleCancel = useCallback(() => {
        setCurrentValue(value);
        setEditing(false);
        setShowSaveButton(false);
        setDropdownOpen(false);
        setHoveredTemplateId(null);
    }, [value]);

    const handleSaveAsTemplate = async () => {
        if (!currentValue || currentValue.trim() === '') {
            message.warning('Please enter a note before saving as template');
            return;
        }

        setIsSavingTemplate(true);
        try {
            const response = await api.post('/note-templates', {
                title: currentValue.length > 50 ? currentValue.substring(0, 47) + '...' : currentValue,
                content: currentValue.trim(),
                category: 'general', // Default category
                tags: ['user-created']
            });

            if (response.data.success) {
                message.success('Note saved as template successfully');
                // Update Redux store with fresh data including the new template
                await dispatch(getDataCommon());
                setShowSaveButton(false);
                setDropdownOpen(true); // Keep dropdown open to show new template
            } else {
                message.error(response.data.message || 'Failed to save note template');
            }
        } catch (error) {
            console.error('Error saving note template:', error);
            message.error('Failed to save note template');
        } finally {
            setIsSavingTemplate(false);
        }
    };

    const handleDeleteTemplate = async (templateId, templateContent) => {
        setIsDeletingTemplate(true);
        try {
            const response = await api.delete(`/note-templates/${templateId}`);

            if (response.data.success) {
                message.success('Note template deleted successfully');
                // Update Redux store to remove the deleted template
                await dispatch(getDataCommon());
                // Keep dropdown open to show updated list
                setDropdownOpen(true);
            } else {
                message.error(response.data.message || 'Failed to delete note template');
            }
        } catch (error) {
            console.error('Error deleting note template:', error);
            message.error('Failed to delete note template');
        } finally {
            setIsDeletingTemplate(false);
        }
    };

    const handleInputChange = (newValue) => {
        setCurrentValue(newValue);
        
        // Show save button if the value is not in existing templates and is not empty
        if (inputType === "note-autocomplete" && newValue && newValue.trim() !== '') {
            const existsInTemplates = noteTemplates.some(t => t.content === newValue);
            setShowSaveButton(!existsInTemplates);
        } else {
            setShowSaveButton(false);
        }
    };

    if (editing) {
        // Render different input types based on inputType prop
        if (inputType === "number") {
            return (
                <InputNumber
                    value={currentValue}
                    onChange={setCurrentValue}
                    onBlur={() => handleSave(currentValue)}
                    onPressEnter={() => handleSave(currentValue)}
                    onKeyDown={(e) => {
                        if (e.key === 'Escape') {
                            handleCancel();
                        }
                    }}
                    style={{ width: '100%' }}
                    placeholder={placeholder}
                    precision={2}
                    min={0}
                    step={0.01}
                    autoFocus
                    prefix={prefix}
                />
            );
        }

        if (inputType === "autocomplete") {
            return (
                <AutoComplete
                    value={currentValue}
                    onChange={setCurrentValue}
                    onBlur={() => handleSave(currentValue)}
                    onPressEnter={() => handleSave(currentValue)}
                    onKeyDown={(e) => {
                        if (e.key === 'Escape') {
                            handleCancel();
                        }
                    }}
                    style={{ width: '100%' }}
                    placeholder={placeholder}
                    options={autoCompleteOptions}
                    filterOption={(inputValue, option) =>
                        option.value.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1
                    }
                    autoFocus
                />
            );
        }

        if (inputType === "note-autocomplete") {
            // Create autocomplete options from Redux noteTemplates with delete functionality
            const autocompleteOptions = [
                ...noteTemplates.map(template => ({
                    value: template.content,
                    label: (
                        <div 
                            style={{ 
                                display: 'flex', 
                                justifyContent: 'space-between', 
                                alignItems: 'center',
                                position: 'relative'
                            }}
                            onMouseEnter={() => setHoveredTemplateId(template.id)}
                            onMouseLeave={() => setHoveredTemplateId(null)}
                        >
                            <span style={{ flex: 1, marginRight: 8 }}>{template.content}</span>
                            <div 
                                style={{ 
                                    opacity: hoveredTemplateId === template.id ? 1 : 0,
                                    transition: 'opacity 0.2s ease',
                                    display: 'flex',
                                    alignItems: 'center'
                                }}
                            >
                                <Popconfirm
                                    title="Delete this template?"
                                    description="This action cannot be undone."
                                    onConfirm={(e) => {
                                        e.stopPropagation();
                                        handleDeleteTemplate(template.id, template.content);
                                    }}
                                    onCancel={(e) => e.stopPropagation()}
                                    okText="Yes"
                                    cancelText="No"
                                    disabled={isDeletingTemplate}
                                >
                                    <Button
                                        size="small"
                                        type="text"
                                        icon={<DeleteOutlined />}
                                        onClick={(e) => {
                                            e.stopPropagation(); // Prevent dropdown from closing
                                        }}
                                        loading={isDeletingTemplate}
                                        style={{ 
                                            color: '#ff4d4f',
                                            border: 'none',
                                            boxShadow: 'none',
                                            padding: '2px 4px',
                                            height: '20px',
                                            minWidth: '20px'
                                        }}
                                    />
                                </Popconfirm>
                            </div>
                        </div>
                    ),
                    key: `template-${template.id}`
                }))
            ];

            // Add save button option at the end if we have a value that's not in templates
            if (showSaveButton && currentValue && currentValue.trim() !== '') {
                autocompleteOptions.push({
                    value: '__save_as_template__', // This should not be selectable as a normal value
                    label: (
                        <div 
                            style={{ 
                                padding: '8px 12px', 
                                borderTop: '1px solid #f0f0f0',
                                cursor: 'pointer',
                                color: '#1890ff',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                            }}
                            onClick={(e) => {
                                e.stopPropagation(); // Prevent normal option selection
                                handleSaveAsTemplate();
                            }}
                        >
                            <PlusOutlined />
                            <span>{isSavingTemplate ? 'Saving...' : 'Save as template'}</span>
                        </div>
                    ),
                    key: 'save-template-option',
                    disabled: isSavingTemplate
                });
            }

            return (
                <AutoComplete
                    value={currentValue}
                    onChange={handleInputChange}
                    onBlur={() => {
                        // Small delay to allow for option selection
                        setTimeout(() => {
                            // Only save if not clicking on the save template option
                            if (currentValue !== '__save_as_template__') {
                                handleSave(currentValue);
                            }
                        }, 150);
                    }}
                    onSelect={(selectedValue) => {
                        if (selectedValue === '__save_as_template__') {
                            handleSaveAsTemplate();
                        } else {
                            // Check if this is not a delete action (i.e., user actually selected the template content)
                            const selectedTemplate = noteTemplates.find(t => t.content === selectedValue);
                            if (selectedTemplate) {
                                handleSave(selectedValue);
                            }
                        }
                    }}
                    onKeyDown={(e) => {
                        if (e.key === 'Escape') {
                            handleCancel();
                        } else if (e.key === 'Enter') {
                            e.preventDefault();
                            handleSave(currentValue); // Save the current typed value, not selected option
                        }
                    }}
                    onDropdownVisibleChange={(open) => {
                        setDropdownOpen(open);
                        if (!open) {
                            setHoveredTemplateId(null);
                        }
                    }}
                    style={{ width: '100%' }}
                    placeholder={placeholder}
                    options={autocompleteOptions}
                    filterOption={(inputValue, option) => {
                        if (option.key === 'save-template-option') return true;
                        return option.value.toLowerCase().indexOf(inputValue.toLowerCase()) !== -1;
                    }}
                    autoFocus
                    open={dropdownOpen}
                    dropdownStyle={{ 
                        maxHeight: 300,
                        minWidth: 450 // Make dropdown wider to accommodate delete button
                    }}
                    allowClear
                />
            );
        }

        if (inputType === "text") {
            return (
                <Input
                    value={currentValue || ''}
                    onChange={(e) => setCurrentValue(e.target.value)}
                    onBlur={() => handleSave(currentValue)}
                    onPressEnter={() => handleSave(currentValue)}
                    onKeyDown={(e) => {
                        if (e.key === 'Escape') {
                            handleCancel();
                        }
                    }}
                    style={{ width: '100%' }}
                    placeholder={placeholder}
                    autoFocus
                />
            );
        }
        
        // Default select input
        return (
            <Select
                value={currentValue}
                onChange={handleSave}
                onBlur={handleCancel}
                onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                        handleCancel();
                    }
                }}
                style={{ width: '100%' }}
                placeholder={placeholder}
                loading={loading}
                autoFocus
                open
            >
                <Option value={null}>
                    <span style={{ color: '#999' }}>Not assigned</span>
                </Option>
                {options.map(option => (
                    <Option key={option.value} value={option.value}>
                        {option.label}
                    </Option>
                ))}
            </Select>
        );
    }

    // Display value when not editing
    if (inputType === "number") {
        return (
            <div
                onClick={() => setEditing(true)}
                className="editable-cell"
                style={{ textAlign: 'right', width: '100%' }} // Right align for number inputs
            >
                <span className="no-line-through" style={{ display: 'block', textAlign: 'right' }}>
                    {currentValue ? 
                        `${prefix}${parseFloat(currentValue).toFixed(2)}` : 
                        <span style={{ color: '#999', fontSize: 11, textAlign: 'right', display: 'block' }}>Click to set</span>
                    }
                </span>
            </div>
        );
    }

    if (inputType === "text" || inputType === "autocomplete" || inputType === "note-autocomplete") {
        return (
            <div
                onClick={() => setEditing(true)}
                className="editable-cell"
            >
                <span className="no-line-through">
                    {currentValue ? 
                        currentValue : 
                        <span style={{ color: '#999', fontSize: 11 }}>Click to enter</span>
                    }
                </span>
            </div>
        );
    }

    // Default display for select - check for custom display component
    return (
        <div
            onClick={() => setEditing(true)}
            className="editable-cell"
            style={{ width: '100%' }}
        >
            {displayComponent && currentValue !== null && currentValue !== undefined ? 
                displayComponent(currentValue) :
                <span className="no-line-through" style={{ width: '100%' }}>
                    {currentValue ? 
                        options.find(opt => opt.value === currentValue)?.label || 'Unknown' : 
                        <span style={{ color: '#999', fontSize: 11, textAlign: 'center' }}>Not assigned</span>
                    }
                </span>
            }
        </div>
    );
});

// Custom comparison function for memo to ensure proper re-rendering
const areEqual = (prevProps, nextProps) => {
    // Compare all props that can affect rendering except displayComponent
    // displayComponent is a function and comparing function references is unreliable
    const propsEqual = (
        prevProps.value === nextProps.value &&
        prevProps.loading === nextProps.loading &&
        prevProps.options === nextProps.options &&
        prevProps.autoCompleteOptions === nextProps.autoCompleteOptions &&
        prevProps.placeholder === nextProps.placeholder &&
        prevProps.inputType === nextProps.inputType &&
        prevProps.prefix === nextProps.prefix &&
        prevProps.record?.id === nextProps.record?.id
    );
    
    // For displayComponent, we rely on value changes to trigger re-renders
    // since displayComponent functions are recreated on each render
    return propsEqual;
};

export default React.memo(EditableCellWithNoteTemplates, areEqual);
